import React, { useId } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';

/**
 * Shared form primitives for full-page EntityList forms (AccountEditor,
 * DebtEditor, ...). Extracted verbatim from AccountEditor.jsx so every
 * entity editor gets the same label/section/control language.
 */

export function RequiredMark() {
	return <span className='tw:ml-1 tw:text-danger'>*</span>;
}

export function FieldLabel({ htmlFor, children, required }) {
	return (
		<label
			htmlFor={htmlFor}
			className='tw:mb-1.5 tw:block tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.08em] tw:text-ink-3'
		>
			{children}
			{required && <RequiredMark />}
		</label>
	);
}

FieldLabel.propTypes = {
	htmlFor: PropTypes.string,
	children: PropTypes.node.isRequired,
	required: PropTypes.bool,
};

export function FormField({ id, label, required, children, className = '' }) {
	return (
		<div className={className}>
			<FieldLabel htmlFor={id} required={required}>
				{label}
			</FieldLabel>
			{children}
		</div>
	);
}

FormField.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string.isRequired,
	required: PropTypes.bool,
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};

export function Section({ title, description, children, className = '' }) {
	return (
		<section className={`tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:shadow-sm ${className}`}>
			<div className='tw:border-b tw:border-border tw:px-5 tw:py-3.5'>
				<h2 className='tw:m-0 tw:text-[13px] tw:font-semibold tw:text-ink-0'>{title}</h2>
				{description && <p className='tw:mt-1 tw:mb-0 tw:text-[12.5px] tw:text-ink-2'>{description}</p>}
			</div>
			<div className='tw:p-5'>{children}</div>
		</section>
	);
}

Section.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string,
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};

export const controlClass =
	'tw:h-[34px] tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface tw:text-[13.5px] tw:text-ink-0 tw:focus:border-accent tw:focus:shadow-none';
export const textareaClass =
	'tw:min-h-[180px] tw:rounded-[7px] tw:border tw:border-border-strong tw:bg-surface tw:text-[13.5px] tw:text-ink-0 tw:focus:border-accent tw:focus:shadow-none';

/**
 * Native <select> styled like every other EntityList form control (matches
 * AccountEditor.jsx's original pattern) — deliberately NOT react-select, to
 * avoid the height/placeholder bugs that come with GenericDropdown's
 * `variant='entity'` mode. `options` accepts plain strings or {value,label}
 * objects, normalized here so either shape can be passed straight through.
 */
export function Select({ id, name, options, value, onChange, placeholder, disabled, className = '' }) {
	const normalized = options.map((option) =>
		typeof option === 'object' && option !== null ? option : { value: option, label: option },
	);
	return (
		<Form.Select
			id={id}
			name={name}
			// `formkit-select` carries a scoped !important border override in
			// globals.css — App.css has a blanket `select { border: none !important }`
			// that an inline style can never beat (inline styles lose to any
			// stylesheet !important, regardless of specificity).
			className={`formkit-select ${controlClass} ${className}`}
			value={value ?? ''}
			onChange={onChange}
			disabled={disabled}
		>
			{placeholder && <option value=''>{placeholder}</option>}
			{normalized.map((option) => (
				<option key={option.value} value={option.value}>
					{option.label}
				</option>
			))}
		</Form.Select>
	);
}

Select.propTypes = {
	id: PropTypes.string,
	name: PropTypes.string,
	options: PropTypes.array.isRequired,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	onChange: PropTypes.func.isRequired,
	placeholder: PropTypes.string,
	disabled: PropTypes.bool,
	className: PropTypes.string,
};

/**
 * A single <label> wrapping the input + text, so clicking the label text
 * toggles the checkbox (the bug with raw react-bootstrap <Form.Check> here is
 * that with no `id`/`controlId` the input and label render as unlinked
 * siblings). Styled natively via Tailwind `accent-*` rather than Bootstrap's
 * `.form-check` skin, which was the source of the flaky checkmark repaint.
 */
export function Checkbox({ id, label, checked, onChange, disabled, className = '' }) {
	const generatedId = useId();
	const checkboxId = id || generatedId;
	return (
		<label
			htmlFor={checkboxId}
			className={`tw:inline-flex tw:items-center tw:gap-2 tw:text-[13px] tw:text-ink-0 ${disabled ? 'tw:cursor-not-allowed tw:opacity-60' : 'tw:cursor-pointer'} ${className}`}
		>
			<input
				type='checkbox'
				id={checkboxId}
				checked={checked}
				onChange={onChange}
				disabled={disabled}
				className='tw:h-4 tw:w-4 tw:rounded tw:border-border-strong tw:accent-accent'
			/>
			<span>{label}</span>
		</label>
	);
}

Checkbox.propTypes = {
	id: PropTypes.string,
	label: PropTypes.node.isRequired,
	checked: PropTypes.bool,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	className: PropTypes.string,
};
